import { useCallback, useEffect, useRef, useState } from "react";
import type { ScheduledMessage, ScheduleMessageRequest } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";
import { notifyChatSummariesStale } from "./chatSummaryBus";

export type ScheduledMessagesState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; messages: ScheduledMessage[] };

export interface ScheduledMessages {
  state: ScheduledMessagesState;
  /** Pending (scheduled or held) messages by send time; empty until loaded. */
  messages: ScheduledMessage[];
  refresh: () => Promise<void>;
  schedule: (req: ScheduleMessageRequest) => Promise<void>;
  update: (id: string, req: ScheduleMessageRequest) => Promise<void>;
  sendNow: (id: string) => Promise<void>;
  cancel: (id: string) => Promise<void>;
}

function pendingBySendAt(messages: ScheduledMessage[]): ScheduledMessage[] {
  return messages
    .filter((m) => m.state === "scheduled" || m.state === "held")
    .sort((a, b) => new Date(a.sendAt).getTime() - new Date(b.sendAt).getTime());
}

export function useScheduledMessages(id: string | undefined): ScheduledMessages {
  const api = useApi();
  const [state, setState] = useState<ScheduledMessagesState>({ status: "loading" });
  const generation = useRef(0);

  const load = useCallback(
    async (patientId: string, gen: number) => {
      try {
        const res = await api.clinicChat.listScheduled(patientId);
        if (generation.current === gen) {
          setState({ status: "success", messages: pendingBySendAt(res.messages) });
        }
      } catch (err) {
        if (generation.current === gen) {
          setState((prev) =>
            prev.status === "success"
              ? prev
              : { status: "error", error: err instanceof Error ? err.message : "Failed to load scheduled messages" },
          );
        }
      }
    },
    [api],
  );

  useEffect(() => {
    generation.current += 1;
    if (!id) {
      return;
    }
    setState({ status: "loading" });
    void load(id, generation.current);
    return () => {
      generation.current += 1;
    };
  }, [id, load]);

  const refresh = useCallback(async () => {
    if (id) {
      await load(id, generation.current);
    }
  }, [id, load]);

  const mutate = useCallback(
    async (run: (patientId: string) => Promise<unknown>) => {
      if (!id) {
        return;
      }
      try {
        await run(id);
      } finally {
        await load(id, generation.current);
        notifyChatSummariesStale();
      }
    },
    [id, load],
  );

  const schedule = useCallback(
    (req: ScheduleMessageRequest) => mutate((pid) => api.clinicChat.schedule(pid, req)),
    [api, mutate],
  );
  const update = useCallback(
    (messageId: string, req: ScheduleMessageRequest) =>
      mutate((pid) => api.clinicChat.updateScheduled(pid, messageId, req)),
    [api, mutate],
  );
  const sendNow = useCallback(
    (messageId: string) => mutate((pid) => api.clinicChat.sendScheduledNow(pid, messageId)),
    [api, mutate],
  );
  const cancel = useCallback(
    (messageId: string) => mutate((pid) => api.clinicChat.cancelScheduled(pid, messageId)),
    [api, mutate],
  );

  return {
    state,
    messages: state.status === "success" ? state.messages : [],
    refresh,
    schedule,
    update,
    sendNow,
    cancel,
  };
}
